import {queries} from '@/shared/integrations/zero'
import {PatientTherapySequence} from '@cypro/engine/schema'
import {type GetPageQuery, type GetSingleQuery} from '@cypro/ui/utils/zero-virtualizer'
import {useCallback, useMemo, useState} from 'react'

export type PatientTherapySequenceStartRow = Pick<PatientTherapySequence, 'id' | 'createdAt' | 'therapyDate' | 'time' | 'medicineNames'>

export function useGetInfinitePatientsTherapiesSequences({
  sort = {dir: 'desc', field: 'createdAt'},
  filters,
  limit = 25,
}: Omit<Partial<Parameters<typeof queries.patientsTherapies.listSequences>[0]>, 'cursor'> = {}) {
  const [search, setSearch] = useState('')

  const listContextParams = useMemo(
    () => ({sort, filters, limit, search}),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [
      sort?.dir,
      sort?.field,
      filters?.search,
      filters?.status,
      filters?.productionStatus,
      filters?.wards,
      filters?.sections,
      filters?.responsibleDoctors,
      filters?.therapyDate,
      filters?.onlyCurrentUserActions,
      filters?.patientArrivalEnabled,
      filters?.patientId,
      limit,
      search,
    ]
  )

  const getPageQuery: GetPageQuery<PatientTherapySequence, PatientTherapySequenceStartRow> = useCallback(
    ({limit: pageLimit, start, dir}) => {
      const cursor = start?.id
        ? {
            id: start.id,
            direction: dir,
            createdAt: start.createdAt,
            therapyDate: start.therapyDate,
            time: start.time,
            medicineNames: start.medicineNames,
          }
        : undefined

      return {
        query: queries.patientsTherapies.listSequences({
          cursor,
          limit: pageLimit,
          sort,
          filters: {...filters, ...(search ? {search} : {})},
        }),
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [listContextParams]
  )

  // @ts-expect-error schema type mismatch (works at runtime)
  const getSingleQuery: GetSingleQuery<PatientTherapySequence> = useCallback(
    ({id}) => ({
      query: queries.patientsTherapies.getSequenceById(id),
    }),
    []
  )

  const toStartRow = useCallback(
    (row: PatientTherapySequence): PatientTherapySequenceStartRow => ({
      id: row.id,
      createdAt: row.createdAt,
      therapyDate: row.therapyDate,
      time: row.time,
      medicineNames: row.medicineNames,
    }),
    []
  )

  const transformToSelectItem = useCallback(
    (row: PatientTherapySequence) => ({label: row.patient?.fullName ?? row.id, value: row.id}),
    []
  )

  return {
    search,
    onSearchChange: setSearch,
    getPageQuery,
    getSingleQuery,
    toStartRow,
    listContextParams,
    transformToSelectItem,
  }
}
